import { Button, Drawer, DrawerProps, Modal, Popconfirm } from 'antd';
import { useTranslate } from '@tolgee/react';
import ExecuteContextsFieldV2, {
  ExecuteContextsFieldV2Props,
} from 'views/Aide/Scenario/PolicyForm/ExecuteContextsFieldV2';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { css } from '@emotion/css';
import { ExtraTabs } from 'views/Aide/Scenario/PolicyForm/TranslateTool';
import { ReactNode, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { getSlateEmptyValue } from 'components/TemplateEditor/utils/slateNodeHelper';
import { Formik, useFormikContext } from 'formik';
import type { DescendantExecuteContext } from 'views/Aide/Scenario/PolicyForm';
import { descendants2segments, segments2descendants } from './helpers';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { forEach, map, find, reduce, get, cloneDeep, set, keys } from 'lodash-es';
import { useLatest, useMemoizedFn, useRequest } from 'ahooks';
import { detectLanguage } from '@knowledge/service/api/general';
import { Descendant } from 'slate';
import { regionLanguageMap } from 'services/config/region';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import mitt from 'mitt';

export interface ValueAsDirectAnswerDraweSelfProps {
  defaultValues?: Record<General.UpperCaseRegion, Knowledge.SPU.Segment[]>;
  regions: General.UpperCaseRegion[];
}

type VAAItem = {
  region: General.UpperCaseRegion;
  v: DescendantExecuteContext;
};

type VAASegmentItem = {
  region: General.UpperCaseRegion;
  v: {
    content: Knowledge.SPU.Segment[];
  };
};

interface VAAFormValue {
  items: VAAItem[];
}

interface ValueAsDirectAnswerDrawerProps extends ValueAsDirectAnswerDraweSelfProps, DrawerProps {
  citeMode?: boolean;
  onDelete?: () => void;
  onConfirm?: (obj: Record<string, Knowledge.SPU.Segment[]>) => void;
  saveLoading?: boolean;
  deleteLoading?: boolean;
  confirmText?: string;
  setDefaultNode?: ReactNode;
}
export const ValueAsDirectAnswerDrawer = (props: ValueAsDirectAnswerDrawerProps) => {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const {
    citeMode,
    onConfirm,
    saveLoading = false,
    deleteLoading = false,
    defaultValues,
    onDelete,
    regions,
    confirmText,
    setDefaultNode = null,
    ...restProps
  } = props;
  const defaultValuesRef = useLatest(defaultValues);
  const initialValues = useMemo<VAAFormValue>(() => {
    return {
      items: regions.map((region) => {
        return {
          region,
          v: {
            content: defaultValues?.[region]
              ? segments2descendants(defaultValues[region])
              : getSlateEmptyValue(),
            translatedContent: getSlateEmptyValue(),
          },
        };
      }),
    };
  }, [defaultValues, regions]);
  const { runAsync, loading: detectLoading } = useRequest<
    { languageCode: string[] },
    [string[], string[], VAASegmentItem[]]
  >((texts) => detectLanguage(texts), {
    manual: true,
    onError: (_, params) => {
      beforeOnConfirm(params[2]);
    },
    onSuccess: (res, params) => {
      const mismatch: string[] = [];
      forEach(res.languageCode, (value, index) => {
        const lang = value;
        const region = params[1][index];
        const langs = detectLangRegionMap[region];

        if (langs && !langs.includes(lang)) {
          mismatch.push(region);
        }
      });

      if (mismatch.length > 0) {
        Modal.confirm({
          title: t('WhatsApp.温馨提示'),
          content: t(
            '{region}站点的内容为其他语言，此内容将直接发送给买家，建议将内容修改为对应的站点语言',
            {
              region: map(mismatch, (region) => getRegionLabel(region)).join(
                langRef.current === 'th' ? ' ' : langRef.current === 'zh-CN' ? '、' : ',',
              ),
            },
          ),
          okText: t('返回修改'),
          cancelText: confirmText || t('继续保存'),
          onOk: () => {},
          onCancel: () => {
            beforeOnConfirm(params[2]);
          },
        });
      } else {
        beforeOnConfirm(params[2]);
      }
    },
  });

  const beforeOnConfirm = useMemoizedFn((segmentsItems: VAASegmentItem[]) => {
    const notEmpty = segmentsItems.filter((each) => each.v.content.length > 0);
    const res = reduce(
      notEmpty,
      (acc, each) => {
        acc[each.region] = each.v.content;
        return acc;
      },
      {} as any,
    );
    onConfirm?.(res);
  });

  const detectLangPrepare = useMemoizedFn((items: VAAItem[]) => {
    const regionArr: string[] = [];
    const textArr: string[] = [];
    const segmentsItems = map(items, (value) => {
      return {
        region: value.region,
        v: {
          content: descendants2segments(value.v.content),
        },
      };
    });
    forEach(segmentsItems, (value) => {
      const text = find(value.v.content, (each) => each.type === 'TEXT');
      if (text) {
        regionArr.push(value.region);
        textArr.push(text.content);
      }
    });
    return runAsync(textArr, regionArr, segmentsItems);
  });
  const lang = useSystemLanguageValue();
  const langRef = useLatest(lang);

  useLayoutEffect(() => {
    if (defaultValuesRef.current === undefined) return;
    const langMap: Record<string, string> = {
      id: 'ID',
      th: 'TH',
      tl: 'PH',
      vi: 'VN',
      pt: 'PT',
    };

    if (langMap[langRef.current]) {
      const target = document.getElementById(`vada-${langMap[langRef.current]}`);
      setTimeout(() => {
        target &&
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
      });
    } else {
      // 找到第一个有内容的
      const firstRegion = get(keys(defaultValuesRef.current), '0');
      const target = document.getElementById(`vada-${firstRegion}`);
      setTimeout(() => {
        target &&
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
      });
    }
  }, [langRef, defaultValuesRef]);

  return (
    <Formik<VAAFormValue> enableReinitialize initialValues={initialValues} onSubmit={() => {}}>
      {({ values, setFieldValue }) => {
        return (
          <Drawer
            title={t('knowledge.知识值')}
            width={1200}
            className={css`
              .ant-drawer-body {
                background: var(--fett-erp-card-bg);
                padding: 16px;
              }
            `}
            footer={
              <div className="flex items-center gap-[16px]">
                <Button
                  loading={saveLoading || detectLoading}
                  onClick={() => {
                    const v = values.items.map((each) => {
                      return {
                        region: each.region,
                        v: {
                          content: descendants2segments(each.v.content),
                        },
                      };
                    });

                    if (citeMode) {
                      beforeOnConfirm(v);
                      return;
                    }

                    let unconfigRegion: VAASegmentItem[] = [];
                    let configRegion: VAASegmentItem[] = [];
                    forEach(v, (each) => {
                      if (each.v.content.length === 0) {
                        unconfigRegion.push(each);
                      } else {
                        configRegion.push(each);
                      }
                    });

                    if (unconfigRegion.length > 0) {
                      Modal.confirm({
                        title: t('有站点未配置知识值'),
                        content: t('{regions}站点未配置知识值，这些站点机器人将无法回答问题', {
                          regions: map(unconfigRegion, ({ region }) => getRegionLabel(region)).join(
                            langRef.current === 'th'
                              ? ' '
                              : langRef.current === 'zh-CN'
                              ? '、'
                              : ',',
                          ),
                        }),
                        okText: confirmText || t('phrase.确认保存'),
                        okButtonProps: {
                          loading: detectLoading,
                        },
                        onOk: () => detectLangPrepare(values.items),
                      });
                    } else {
                      detectLangPrepare(values.items);
                    }
                  }}
                  type="primary"
                >
                  {confirmText || t('phrase.保存')}
                </Button>
                <Button
                  onClick={() => {
                    props.onClose?.(null!);
                  }}
                >
                  {t('phrase.取消')}
                </Button>
                {onDelete && (
                  <Button
                    loading={deleteLoading}
                    onClick={() => onDelete()}
                    type="text"
                    icon={<DeleteOutlined />}
                    className="ml-[-16px]"
                  >
                    {t('phrase.删除')}
                  </Button>
                )}
                {setDefaultNode}
              </div>
            }
            {...restProps}
          >
            <>
              {values.items.map((each, index) => {
                return (
                  <RegionField
                    citeMode={citeMode}
                    region={each.region}
                    key={each.region}
                    index={index}
                    hideCopy={values.items.length === 1}
                    value={each.v}
                    onChange={(v) => {
                      setFieldValue(`items.${index}.v`, v);
                    }}
                    error={{}}
                  ></RegionField>
                );
              })}
            </>
          </Drawer>
        );
      }}
    </Formik>
  );
};

const refreshBus = mitt();

interface RegionFieldProps extends Omit<ExecuteContextsFieldV2Props, 'msgTypes'> {
  citeMode?: boolean;
  hideCopy?: boolean;
  index: number;
}
const RegionField = (props: RegionFieldProps) => {
  const { citeMode, region, index, hideCopy = false, value, onChange, ...restProps } = props;
  const onChangeRef = useLatest(onChange);
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const { setFieldValue, values } = useFormikContext<VAAFormValue>();
  const translateDisabled = useMemo(() => {
    return !find(value?.content, (each) => {
      if (each.type === 'paragraph') {
        return !!get(each, 'children.0.text');
      }
      if (each.type === 'image') {
        return each.images?.length > 0;
      }
      return false;
    });
  }, [value]);
  const [refreshId, setRefreshId] = useState(1);
  useEffect(() => {
    refreshBus.on('refresh', () => {
      setRefreshId((v) => v + 1);
    });
    return () => {
      refreshBus.off('refresh');
    };
  }, []);
  const handleCopy = useMemoizedFn(() => {
    forEach(values.items, async (each, i) => {
      if (i !== index) {
        const content = cloneDeep(value?.content);
        const texts = reduce<Descendant, string[]>(
          content,
          (acc, each) => {
            if (each.type === 'paragraph') {
              acc.push(get(each, 'children.0.text', ''));
            }
            return acc;
          },
          [],
        );
        if (texts.length === 0) {
          setFieldValue(`items.${i}.v`, {
            content,
            translatedContent: getSlateEmptyValue(),
          });
          refreshBus.emit('refresh');
        } else {
          const overwriteRegionLanguageMap = {
            ...regionLanguageMap,
            ph: 'tl',
            my: 'ms',
          } as Record<General.Region, General.Language>;
          const targetLang =
            overwriteRegionLanguageMap[each.region.toLowerCase() as General.Region] || 'en-US';
          try {
            const res = await overseaTranslate(texts, targetLang, 'singleClass', each.region);
            if (res) {
              forEach(content, (a, i) => {
                if (a.type === 'paragraph') {
                  set(a, 'children.0.text', res[i]);
                }
              });
            }
            setFieldValue(`items.${i}.v`, {
              content,
              translatedContent: getSlateEmptyValue(),
            });
            refreshBus.emit('refresh');
          } catch (_) {}
        }
      }
    });
  });

  return (
    <div id={`vada-${region}`}>
      <div className="bg-[var(--fett-bg-panel)] mb-[16px] flex p-[16px]">
        <div className="mr-[24px] border-0 border-r border-solid border-[#eee] flex-grow pr-[24px]">
          <div className="text-[16px] font-bold mb-[12px] flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-[14px] w-[4px] bg-[#3370ff] mr-[8px]"></div>
              <div>
                {t('aide.发送内容')}&nbsp;{!citeMode && '-'}&nbsp;
              </div>
              {!citeMode && <div>{getRegionLabel(region)}</div>}
            </div>
            {!hideCopy && (
              <Popconfirm
                disabled={translateDisabled}
                title={t('复制内容后，当前站点的内容会覆盖其他站点的内容')}
                okText={t('phrase.确定')}
                cancelText={t('phrase.取消')}
                onConfirm={() => handleCopy()}
              >
                <Button type="link" disabled={translateDisabled} style={{ paddingRight: 0 }}>
                  <CopyOutlined />
                  {t('aide.复用内容至其他全部站点')}
                </Button>
              </Popconfirm>
            )}
          </div>
          <ExecuteContextsFieldV2
            key={`${region}-${refreshId}`}
            refresh={refreshId}
            region={region}
            value={value}
            msgTypes={['text', 'image', 'video']}
            onChange={(v) => {
              onChange(v);
            }}
            error={undefined}
            {...restProps}
          />
        </div>

        <div className="flex-shrink-0">
          <ExtraTabs
            region={region}
            allowDividing={false}
            value={value!}
            onChange={(v) => {
              onChangeRef.current?.(v);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const detectLangRegionMap: Record<string, string[]> = {
  ID: ['id'],
  TH: ['th'],
  MY: ['ms', 'en'],
  PH: ['tl', 'en', 'fil'],
  SG: ['en'],
  VN: ['vi'],
  BR: ['pt'],
  US: ['en'],
};
